import React, { forwardRef } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useDocuments } from 'contexts/documents'
import { useCombineAccessUnits, useCategories, useFormatGenericAccess } from 'pages/documents/utilities'
import { useMe } from 'contexts/me'
import { useFlash } from 'hooks/flash'
import { mergeRefs } from 'react-merge-refs'
import { onClink } from 'utilities/event'
import { getSmartDocumentUrl } from 'utilities/url'
import { pruneBy } from 'utilities/array'
import { getFullName } from 'utilities/person'
import { cls } from 'utilities/dom'
import {
    Document,
    BulkCell,
    NameCell, FileNameIcon,
    CreatedCell, AccessCell, CategoryCell, ActionsCell, SigningStatus,
    EditLink, ConcernsSummary, Units, AccessSummary, Category,
    MediaQuery
} from './s'
import Paragraph from 'components/typography/paragraph'
import Checkbox from 'components/form/input/checkbox'
import Tooltip from 'components/tooltip'
import CustomTooltip from 'components/form/field/access-units/custom-tooltip'
import WhoHasAccess from 'components/who-has-access'
import RelativeTime from 'components/time-relative'
import ContextMenu, { getActionsArray } from 'widgets/context-menu'

const DocumentItem = forwardRef(({ dokument, actions = [], columns, flash }, ref) => {
    const { formatMessage } = useIntl()

    const {
        dokuments = [],
        selectable = false,
        lastSelected = [],
        selection,

        selectDocument,
        selectMultipleDocuments,
        deselectDocument
    } = useDocuments()

    const { isItMyOwnId } = useMe()

    const combineAccessUnits = useCombineAccessUnits()
    const { getCategoryLabel } = useCategories()
    const getFormattedGenericAccess = useFormatGenericAccess()
    const flasher = useFlash(flash)

    const {
        id,
        type = 'file',
        uploadedAt,
        uploadedBy,
        owners = [],
        concerns,
        shares = [],
        signing
    } = dokument

    const selected = !!selection.find(dokument => dokument.id === id)
    const accesses = pruneBy([...owners, ...shares])
    const publicAccess = accesses.find(({ type }) => type === 'organization')
    const userShares = accesses.filter(({ type }) => type === 'user')
    const unshared = accesses.length === 1 && isItMyOwnId(accesses[0].id)

    const salt = `document:${id}`

    let concernsName
    if(concerns) {
        concernsName = formatMessage({
            id: 'document_regarding_person',
            defaultMessage: 'Regarding {person}'
        }, { person: getFullName(concerns) })
    }

    const className = cls([
        selectable && 'bulk-document',
        signing && 'signature',
        concernsName && 'concerns',
        selected && 'selected'
    ])

    const toggleSelected = e => {
        if(e.target.checked) {
            selectDocument(id)
        } else {
            deselectDocument(id)
        }
    }

    const url = (type === 'smart') ?
        getSmartDocumentUrl(dokument) :
        `${global.location.pathname}?id=${id}`

    return (
        <Document
            {...(className ? { className } : null)}
            ref={mergeRefs([ref, flasher])}>
            {!!selectable && (
                <BulkCell>
                    <Checkbox
                        name="selected"
                        id={`documents:bulk:selected:${id}`}
                        checked={selected}
                        readOnly={true}
                        onClick={e => {
                            if(!!e?.shiftKey) {
                                const lastSelectedId = lastSelected.at(-1)
                                const lastSelectedIndex = dokuments.findIndex(dokument => dokument.id === lastSelectedId)
                                const clickedIndex = dokuments.findIndex(dokument => dokument.id === id)

                                if(lastSelectedIndex > -1 && clickedIndex > -1) {
                                    const range = dokuments
                                        .slice(
                                            Math.min(lastSelectedIndex, clickedIndex),
                                            Math.max(lastSelectedIndex, clickedIndex) + 1
                                        )
                                        .map(({ id }) => id)
                                        .filter(id => !selection.find(({ id: selectedId }) => selectedId === id))

                                    selectMultipleDocuments(range, id)
                                } else if(!lastSelected.length) {
                                    selectDocument(id)
                                }

                                return
                            }

                            toggleSelected(e)
                        }} />
                </BulkCell>
            )}
            <NameCell>
                <EditLink
                    to={url}
                    onClick={onClink(actions.preview?.(dokument)?.onClick)}>
                    <FileNameIcon
                        name={dokument.name}
                        extension={dokument.extension ?? dokument.type}
                        inline={true} />
                    {concernsName && (
                        <ConcernsSummary className="compact">{concernsName}</ConcernsSummary>
                    )}
                </EditLink>
            </NameCell>
            {columns.map(({ id }) => {
                const Cell = {
                    uploadedAt: CreatedCell,
                    access: AccessCell,
                    category: CategoryCell
                }[id]

                const Content = {
                    uploadedAt: () => (
                        <>
                            <span>
                                <RelativeTime date={uploadedAt} />
                            </span>
                            <Paragraph className="caption small compact">
                                <FormattedMessage
                                    id="byline"
                                    defaultMessage="By {name}"
                                    values={{ name: getFullName(uploadedBy) }} />
                            </Paragraph>
                        </>
                    ),
                    access: () => {
                        const formattedGenericAccess = getFormattedGenericAccess({ shares, owners })

                        return (
                            <>
                                {!!unshared && (
                                    <AccessSummary className="compact">
                                        <FormattedMessage
                                            id="access_only_you"
                                            defaultMessage="Only you have access" />
                                    </AccessSummary>
                                )}
                                {(!unshared && !publicAccess && userShares?.length === accesses.length) && (
                                    <MediaQuery className="from-phone">
                                        <Tooltip
                                            content={(
                                                <CustomTooltip
                                                    value={combineAccessUnits(dokument)}
                                                    salt={salt} />
                                            )}
                                            placement="top-end">
                                            <WhoHasAccess
                                                people={pruneBy([...owners, ...userShares])}
                                                max={5}
                                                size={32}
                                                showTooltip={false}
                                                salt={salt} />
                                        </Tooltip>
                                    </MediaQuery>
                                )}
                                {(!unshared && (!!publicAccess || userShares?.length !== accesses.length)) && (
                                    <MediaQuery className="from-phone">
                                        <Tooltip
                                            content={(
                                                <CustomTooltip
                                                    value={combineAccessUnits(dokument)}
                                                    salt={salt} />
                                            )}
                                            placement="top-end">
                                            <Units
                                                units={accesses.map(owner => owner.type === 'organization' ? { ...owner, type: 'company' } : owner)}
                                                max={2}
                                                showTooltip={false} />
                                        </Tooltip>
                                    </MediaQuery>
                                )}
                                {!unshared && (
                                    <MediaQuery className="below-phone">
                                        <AccessSummary className="compact">
                                            {formattedGenericAccess}
                                        </AccessSummary>
                                    </MediaQuery>
                                )}
                            </>
                        )
                    },
                    category: () => (
                        <Category>
                            {getCategoryLabel(dokument.category ?? 'none')}
                        </Category>
                    )
                }[id]

                return (
                    <Cell
                        {...(!concernsName ? { className: 'no-concerns' } : null)}
                        key={`${salt}:column:${id}`}>
                        <Content />
                    </Cell>
                )
            })}
            {!!getActionsArray(actions)?.length && (
                <ActionsCell>
                    <ContextMenu
                        salt={salt}
                        context={dokument}
                        actions={actions} />
                </ActionsCell>
            )}
            <SigningStatus
                withIcon={true}
                signing={signing} />
        </Document>
    )
})

export default DocumentItem